import { useQuery } from '@apollo/client';
import { STUDIOS_FOR_USER_PROFILE, STUDIOS_FOR_SERVICE_SELECTION } from '../graphql/queries/queries';
import { useMemo } from 'react';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import awsConfig from '../config/aws-config';
import { filterHostVisibleStudios } from '../util/studioStatus';

export const useUser = (userId) => {
  // Use a minimal studio query that does NOT request pricePerHour.
  // This avoids GraphQL errors for studios where pricePerHour is null,
  // while still providing all fields needed in the UI (images, User, etc.).
  const { data, loading, error } = useQuery(STUDIOS_FOR_USER_PROFILE, {
    variables: { 
      userID: userId,
      limit: 100
    },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  // Process all data including image URLs
  const processedData = useMemo(() => {
    if (!data?.studiosByUserID?.items) return null;

    const studios = filterHostVisibleStudios(data.studiosByUserID.items).map(studio => {
      const processedStudio = { ...studio };

      // Process studio images
      if (processedStudio.images && Array.isArray(processedStudio.images)) {
        processedStudio.processedImages = processedStudio.images.map(imageKey => {
          if (!imageKey) return '';

          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 1200,
              height: 800,
              fit: 'cover'
            });
          } catch (error) {
            try {
              const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
              return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
            } catch (fallbackError) {
              return '';
            }
          }
        });
      } else {
        processedStudio.processedImages = [];
        processedStudio.images = [];
      }

      processedStudio.hasImages = processedStudio.images && processedStudio.images.length > 0;

      // Process User data if it exists
      if (processedStudio.User?.profilePicture) {
        let processedProfilePicture = "";

        try {
          processedProfilePicture = getCloudFrontImageUrl(processedStudio.User.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          console.error('Error processing user profile picture:', error);
          processedProfilePicture = "";
        }

        processedStudio.User = {
          ...processedStudio.User,
          processedProfilePicture
        };
      }

      return processedStudio;
    });

    const userData = studios[0]?.User || null;

    return {
      user: userData,
      studios: studios,
      hasStudios: studios.length > 0
    };
  }, [data]);

  return {
    userData: processedData,
    loading,
    error
  };
};

/**
 * Lightweight variant for ServicesStudiosPage:
 * only computes one cover image per studio instead of processing all images.
 */
export const useUserStudiosPreview = (userId) => {
  const { data, loading, error } = useQuery(STUDIOS_FOR_SERVICE_SELECTION, {
    variables: {
      userID: userId,
      limit: 100,
    },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  const processedData = useMemo(() => {
    if (!data?.studiosByUserID?.items) return null;

    const studios = data.studiosByUserID.items.map((studio) => {
      const firstImageKey = Array.isArray(studio?.images) ? studio.images[0] : '';
      let processedCoverImage = '';

      if (firstImageKey) {
        try {
          processedCoverImage = getCloudFrontImageUrl(firstImageKey, {
            width: 700,
            height: 500,
            fit: 'cover',
            quality: 70,
          });
        } catch (error) {
          try {
            const cleanKey = firstImageKey.startsWith('public/') ? firstImageKey : `public/${firstImageKey}`;
            processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            processedCoverImage = '';
          }
        }
      }

      return {
        ...studio,
        processedCoverImage,
        hasImages: Boolean(firstImageKey),
      };
    });

    return {
      studios,
      hasStudios: studios.length > 0,
    };
  }, [data]);

  return {
    userData: processedData,
    loading,
    error,
  };
};